// R1-VISUAL Batch 3 — Slice 7a: Quote reskin to the unified brand palette
// (ivory / navy / brass). Backend flow (POST /public/leads) is byte-for-byte
// identical to the previous file. All data-testids preserved.
import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { API } from "../lib/api";
import { CheckCircle2, Phone, PhoneCall, ArrowLeft } from "lucide-react";

const LOGO = "/brand/erf_logo_horizontal.jpg";

const SECTORS = [
  { key: "residential", label: "Residential" },
  { key: "commercial", label: "Commercial" },
  { key: "industrial", label: "Industrial" },
  { key: "government", label: "Government" },
  { key: "property_management", label: "Property Management" },
];

export default function Quote() {
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    address_line1: "", city: "", state: "PA", zip: "",
    sector: "residential", project_type: "", budget_range: "",
    timeframe: "", preferred_contact: "email",
    source: "Website", notes: "", honeypot: "",
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const r = await axios.post(`${API}/public/leads`, form);
      setResult(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Unable to submit. Please call the office.");
    } finally { setBusy(false); }
  };

  const labelStyle = {
    fontFamily: '"Inter", sans-serif', fontSize: 11, fontWeight: 600,
    letterSpacing: "0.18em", textTransform: "uppercase",
    color: "#0B2545", opacity: 0.72,
  };
  const inputStyle = {
    background: "#FFFFFF", border: "1px solid #E5E1DA",
    color: "#0B2545", height: 44, fontFamily: '"Inter", sans-serif',
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      data-testid="quote-page"
      style={{ background: "#F5F1E8", fontFamily: '"Inter", system-ui, sans-serif', color: "#1F2937" }}
    >
      {/* Header */}
      <header
        style={{
          padding: "20px 24px",
          background: "#FFFFFF",
          borderBottom: "1px solid #E5E1DA",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}
      >
        <Link to="/" data-testid="quote-brand-link" style={{ display: "inline-flex", alignItems: "center", gap: 14 }}>
          <img src={LOGO} alt="ERF ENTERPRISES LLC" style={{ height: 42, width: "auto", display: "block" }} />
          <div>
            <div
              style={{
                fontFamily: '"IBM Plex Mono", monospace',
                fontSize: 10, letterSpacing: "0.28em", textTransform: "uppercase", color: "#B08D57",
              }}
            >
              Request a Quote
            </div>
            <div style={{ fontFamily: '"Playfair Display", serif', fontSize: 14, color: "#0B2545", fontWeight: 600 }}>
              Free Consultation · One-Business-Day Response
            </div>
          </div>
        </Link>
        <Link
          to="/"
          data-testid="quote-back"
          style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            border: "1px solid #E5E1DA", padding: "8px 16px",
            fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase",
            color: "#0B2545", background: "#FFFFFF", borderRadius: 3,
          }}
        >
          <ArrowLeft size={14} /> Home
        </Link>
      </header>

      {/* Body */}
      <main
        style={{
          flex: 1,
          maxWidth: 880, width: "100%",
          margin: "0 auto",
          padding: "56px 24px 64px",
        }}
      >
        {result ? (
          <div
            data-testid="quote-success"
            style={{
              background: "#FFFFFF",
              border: "1px solid #E5E1DA",
              borderLeft: "3px solid #B08D57",
              borderRadius: 4,
              padding: 40,
              display: "flex", flexDirection: "column", gap: 18,
              boxShadow: "0 20px 40px -30px rgba(11,37,69,0.35)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <CheckCircle2 size={40} color="#5B7C6E" strokeWidth={1.5} />
              <div>
                <div style={{ fontFamily: '"Playfair Display", serif', fontSize: 28, color: "#0B2545", fontWeight: 700 }}>
                  Request received
                </div>
                <div style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: 12, letterSpacing: "0.16em", color: "#B08D57", marginTop: 4 }}>
                  Reference {result.lead_number}
                </div>
              </div>
            </div>
            <p style={{ fontSize: 15, lineHeight: 1.7, color: "#1F2937" }}>{result.message}</p>
            <div style={{ borderTop: "1px solid #E5E1DA", paddingTop: 16, display: "flex", flexDirection: "column", gap: 6 }}>
              {result.contact?.phone_office && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: '"IBM Plex Mono", monospace', color: "#0B2545" }}>
                  <PhoneCall size={16} color="#B08D57" /> {result.contact.phone_office}
                </div>
              )}
              {result.contact?.phone_mobile && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: '"IBM Plex Mono", monospace', color: "#0B2545" }}>
                  <Phone size={16} color="#B08D57" /> {result.contact.phone_mobile}
                </div>
              )}
            </div>
            <Link
              to="/"
              data-testid="quote-home-btn"
              style={{
                marginTop: 8, alignSelf: "flex-start",
                background: "#B08D57", color: "#0B2545",
                padding: "12px 26px", fontSize: 12, fontWeight: 700,
                letterSpacing: "0.16em", textTransform: "uppercase", borderRadius: 3,
              }}
            >
              Back to home
            </Link>
          </div>
        ) : (
          <div
            style={{
              background: "#FFFFFF",
              border: "1px solid #E5E1DA",
              borderRadius: 4,
              boxShadow: "0 20px 40px -30px rgba(11,37,69,0.28)",
              padding: 40,
            }}
          >
            <div style={{ marginBottom: 28 }}>
              <div style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: 11, letterSpacing: "0.28em", textTransform: "uppercase", color: "#B08D57" }}>
                Free consultation
              </div>
              <h1 style={{ marginTop: 10, fontFamily: '"Playfair Display", serif', fontSize: 32, color: "#0B2545", fontWeight: 700, lineHeight: 1.15 }}>
                Tell us about your project
              </h1>
              <p style={{ marginTop: 10, fontSize: 14, color: "#1F2937", opacity: 0.82, lineHeight: 1.65 }}>
                We respond within one business day. Operating in PA · NJ · DE · MD · DC · VA · NC · SC · GA · FL.
              </p>
            </div>

            <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <input type="text" value={form.honeypot} onChange={(e) => setForm({ ...form, honeypot: e.target.value })} name="website_url_hp" className="hidden" tabIndex="-1" autoComplete="off" />

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }} className="erf-quote-row">
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <Label style={labelStyle}>First name</Label>
                  <Input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} style={inputStyle} data-testid="q-first" />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <Label style={labelStyle}>Last name</Label>
                  <Input required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} style={inputStyle} data-testid="q-last" />
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }} className="erf-quote-row">
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <Label style={labelStyle}>Email</Label>
                  <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} style={{ ...inputStyle, fontFamily: '"IBM Plex Mono", monospace' }} data-testid="q-email" />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <Label style={labelStyle}>Phone</Label>
                  <Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} style={{ ...inputStyle, fontFamily: '"IBM Plex Mono", monospace' }} data-testid="q-phone" />
                </div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <Label style={labelStyle}>Property address</Label>
                <Input value={form.address_line1} onChange={(e) => setForm({ ...form, address_line1: e.target.value })} style={inputStyle} data-testid="q-address" />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }} className="erf-quote-row">
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <Label style={labelStyle}>City</Label>
                  <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} style={inputStyle} data-testid="q-city" />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <Label style={labelStyle}>State</Label>
                  <Select value={form.state} onValueChange={(v) => setForm({ ...form, state: v })}>
                    <SelectTrigger data-testid="q-state" style={inputStyle}><SelectValue /></SelectTrigger>
                    <SelectContent>{["PA","NJ","DE","MD","DC","VA","NC","SC","GA","FL"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <Label style={labelStyle}>Zip</Label>
                  <Input value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} style={{ ...inputStyle, fontFamily: '"IBM Plex Mono", monospace' }} data-testid="q-zip" />
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }} className="erf-quote-row">
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <Label style={labelStyle}>Sector</Label>
                  <Select value={form.sector} onValueChange={(v) => setForm({ ...form, sector: v })}>
                    <SelectTrigger data-testid="q-sector" style={inputStyle}><SelectValue /></SelectTrigger>
                    <SelectContent>{SECTORS.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <Label style={labelStyle}>Project type</Label>
                  <Input value={form.project_type} onChange={(e) => setForm({ ...form, project_type: e.target.value })} placeholder="Kitchen, addition, new build…" style={inputStyle} data-testid="q-project-type" />
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }} className="erf-quote-row">
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <Label style={labelStyle}>Budget range</Label>
                  <Input value={form.budget_range} onChange={(e) => setForm({ ...form, budget_range: e.target.value })} style={inputStyle} data-testid="q-budget" />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <Label style={labelStyle}>Timeframe</Label>
                  <Input value={form.timeframe} onChange={(e) => setForm({ ...form, timeframe: e.target.value })} style={inputStyle} data-testid="q-timeframe" />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <Label style={labelStyle}>Contact by</Label>
                  <Select value={form.preferred_contact} onValueChange={(v) => setForm({ ...form, preferred_contact: v })}>
                    <SelectTrigger data-testid="q-contact" style={inputStyle}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="phone">Phone</SelectItem>
                      <SelectItem value="text">Text</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <Label style={labelStyle}>Project description</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={4} style={{ ...inputStyle, height: "auto", padding: 12 }} data-testid="q-notes" />
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <Label style={labelStyle}>How did you hear about us? (optional)</Label>
                <Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} style={inputStyle} data-testid="q-source" />
              </div>

              <div
                style={{
                  borderTop: "1px solid #E5E1DA", paddingTop: 12,
                  fontFamily: '"IBM Plex Mono", monospace',
                  fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase",
                  color: "#64748B",
                }}
              >
                Anti-spam: <span style={{ color: "#B08D57" }}>challenge not configured</span>. No challenge is shown until an anti-spam provider is configured.
              </div>

              {err && (
                <div
                  data-testid="quote-error"
                  style={{
                    background: "#FDECEC", border: "1px solid #F5B7B1",
                    borderLeft: "3px solid #A93226",
                    color: "#7B241C", padding: "10px 12px", borderRadius: 3, fontSize: 13,
                  }}
                >
                  {err}
                </div>
              )}

              <Button
                type="submit" disabled={busy}
                data-testid="quote-submit"
                style={{
                  width: "100%", height: 48,
                  background: "#B08D57", color: "#0B2545",
                  fontFamily: '"Inter", sans-serif',
                  fontSize: 12, fontWeight: 700,
                  letterSpacing: "0.18em", textTransform: "uppercase",
                  borderRadius: 3, border: "none",
                  boxShadow: "0 10px 24px -14px rgba(176,141,87,0.6)",
                  cursor: busy ? "not-allowed" : "pointer",
                }}
              >
                {busy ? "Submitting…" : "Submit request"}
              </Button>

              <div
                style={{
                  textAlign: "center",
                  fontFamily: '"IBM Plex Mono", monospace',
                  fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase",
                  color: "#64748B",
                }}
              >
                Or call directly:{" "}
                <a href="tel:+18002406404" data-testid="quote-call" style={{ color: "#B08D57", textDecoration: "none", fontWeight: 700 }}>
                  (800) 240-6404
                </a>
              </div>

              <div
                style={{
                  textAlign: "center", fontSize: 11, color: "#64748B", marginTop: -4,
                }}
              >
                By submitting, you agree to our{" "}
                <Link to="/terms-and-conditions" style={{ color: "#0B2545", textDecoration: "underline" }}>Terms &amp; Conditions</Link>{" "}
                and{" "}
                <Link to="/privacy-policy" style={{ color: "#0B2545", textDecoration: "underline" }}>Privacy Policy</Link>.
              </div>
            </form>
          </div>
        )}
      </main>

      <style>{`
        @media (max-width: 640px) {
          .erf-quote-row { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
